import { ArrowRight, Mail, Loader2, Globe2 } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { RegistrationCards } from "@/components/RegistrationCards";
import { partitionEventsByCountry, type RegistrationEvent } from "@/lib/registrationUtils";
import { useState } from "react";
import { subscribeForUpdates } from "@/api/persons";
import { useAllCities } from "@/hooks/useAllCities";
import { countryToFlag, isoToCountryName, COUNTRY_NAMES } from "@/lib/countryFlags";
import { logger, serializeError } from "@/lib/logger";

// Shared input/select styling so all fields in the notify form match.
const inputClass =
  "w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground text-sm placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/60 transition-all";

// Filled orange CTA (virtual Join, and the subscribe button when it's the only option).
const btnFilled =
  "w-full inline-flex items-center justify-center px-4 py-3 rounded-xl bg-brand-orange text-background text-sm font-bold hover:bg-[hsl(30_90%_50%)] transition-all active:scale-[0.98] shadow-lg shadow-orange-500/20";

interface NextEventSectionProps {
  registrationEvents: RegistrationEvent[];
}

export const NextEventSection = ({ registrationEvents }: NextEventSectionProps) => {
  // Geo filter: ?c=<ISO> (e.g. ?c=GB) shows that country's onsite events + all
  // virtual by default; other countries collapse behind a toggle. Short ISO code
  // in the URL (not the full name) — stable, locale-independent, no encoding.
  const [searchParams] = useSearchParams();
  const isoParam = (searchParams.get("c") || "").trim();
  const activeCountry = isoParam ? isoToCountryName(isoParam) : "";
  const [showOther, setShowOther] = useState(false);

  const { inCountry, other } = partitionEventsByCountry(
    registrationEvents,
    activeCountry || null,
  );
  const hasOther = other.length > 0;
  const visibleEvents = showOther ? registrationEvents : inCountry;

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  // Single typeahead: user types a city; matching known cities fill in their
  // country, matching countries fill the country field. The country dropdown is
  // always editable so an inferred country can be overridden. `cityOpen` toggles
  // the suggestion dropdown.
  const [cityInput, setCityInput] = useState("");
  const [country, setCountry] = useState("");
  const [cityOpen, setCityOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The live virtual event powers option ①; hide that option when there's none.
  const virtualEvent = registrationEvents.find((evt) => evt.isVirtual);

  // All distinct city/country pairs from the DB (every location ever used,
  // onsite or virtual) — v_location, not just onsite cities.
  const { cities: knownCities } = useAllCities();

  // City typeahead: suggest matching cities once 3+ chars are typed (substring
  // match). Picking one fills the city + pre-fills its country in the dropdown.
  const query = cityInput.trim().toLowerCase();
  const suggestions =
    query.length >= 3
      ? knownCities
          .filter((c) => c.city.toLowerCase().includes(query))
          .slice(0, 8)
      : [];
  const cityValid = !!cityInput.trim() && !!country;

  const handleNotify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      // Home city is required; country comes from the editable dropdown (which a
      // city/country suggestion pre-fills). An already-registered email is a
      // silent success server-side.
      const { error: subscribeErr } = await subscribeForUpdates({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim().toLowerCase(),
        homeCity: cityInput.trim(),
        homeCountry: country,
      });

      if (subscribeErr) throw subscribeErr;
      setCityInput("");
      setCountry("");
      setSubmitted(true);
    } catch (err: unknown) {
      logger.error("Notify form error", serializeError(err));
      const msg =
        err instanceof Error
          ? err.message
          : "Something went wrong. Please try again.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="next-event" className="py-16 md:py-24 relative">
      {/* Accent gradient strip at top */}
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-brand-orange via-[hsl(var(--primary))] to-brand-orange" />

      {/* Subtle gradient accent */}
      <div className="absolute inset-0 bg-gradient-to-b from-brand-orange/[0.03] via-transparent to-transparent pointer-events-none" />

      <div className="container mx-auto px-5 md:px-8 relative">
        {/* Section header */}
        <div className="max-w-2xl">
          <div className="text-xs font-bold uppercase tracking-widest text-brand-orange mb-2">
            Next event
          </div>
          <h2 className="text-2xl md:text-4xl font-black tracking-tight text-foreground">
            Ready to race?
          </h2>
          <p className="mt-3 text-muted-foreground text-base leading-relaxed">
            Events happen in parks across London and beyond. In-person or virtual — your choice.
          </p>
          {activeCountry && hasOther && (
            <div className="mt-5 inline-flex rounded-full border border-border bg-card p-1" role="tablist" aria-label="Filter events by location">
              <button
                type="button"
                role="tab"
                aria-selected={!showOther}
                onClick={() => setShowOther(false)}
                className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold transition-colors ${
                  !showOther
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {countryToFlag(activeCountry)} {activeCountry}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={showOther}
                onClick={() => setShowOther(true)}
                className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold transition-colors ${
                  showOther
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Globe2 className="h-4 w-4" /> All events
              </button>
            </div>
          )}
        </div>

        <div className="mt-8 grid lg:grid-cols-[1fr_380px] gap-8 items-start">
          {/* Event cards — reuse existing RegistrationCards. Virtual shows BOTH
              inline per quizrun AND as the sidebar CTA (intentional duplication). */}
          <div>
            {visibleEvents.length > 0 ? (
              <RegistrationCards events={visibleEvents} showHeader={false} maxQuizruns={2} flat />
            ) : (
              <div className="rounded-2xl border border-border bg-card p-8 text-center">
                <div className="text-4xl mb-4"><span aria-hidden="true" style={{ display: "inline-block", transform: "scaleX(-1)" }}>🏃‍♂️</span></div>
                <div className="text-lg font-bold text-foreground">Next event coming soon</div>
                <p className="text-sm text-muted-foreground mt-2">
                  Drop your details below and you'll be first to know when we add new events.
                </p>
              </div>
            )}

            {/* All events + results link */}
            <div className="mt-6">
              <Link to="/events" className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors group">
                View all events & results
                <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </div>
          </div>

          {/* Get-notified card — two ways in: run virtually now (①), or
              subscribe for future events (②). */}
          <div id="notify-form" className="rounded-2xl border border-border bg-card p-6 md:p-8 sticky top-24">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-brand-orange/15 flex items-center justify-center text-brand-orange">
                <Mail className="h-5 w-5" />
              </div>
              <div>
                <div className="text-sm font-bold text-foreground">Don't see a date or city that works?</div>
              </div>
            </div>

            {submitted ? (
              <div className="rounded-xl bg-green-50 dark:bg-green-950/30 border border-green-200/50 dark:border-green-800/30 p-4 text-center">
                <div className="text-lg mb-1">✅</div>
                <div className="text-sm font-bold text-green-700 dark:text-green-400">You're on the list!</div>
                <div className="text-xs text-green-600/70 dark:text-green-500/70 mt-1">You'll be first to know about new events.</div>
              </div>
            ) : (
              <>
                {/* Option ① — run virtually (primary). Only when a live virtual
                    event exists; link logic mirrors VirtualRow. */}
                {virtualEvent && (
                  <div className="rounded-xl border border-brand-orange/30 bg-brand-orange/[0.06] p-4 mb-4">
                    <div className="text-sm font-bold text-foreground">Run virtually — anytime, anywhere</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Same questions, your own route, on your schedule.
                    </div>
                    <Link
                      to={`/events/register?event_id=${virtualEvent.eventId}`}
                      className={`mt-3 ${btnFilled}`}
                    >
                      Join virtually →
                    </Link>
                  </div>
                )}

                {/* Divider between the two options (only when ① is shown). */}
                {virtualEvent && (
                  <div className="flex items-center gap-3 mb-4">
                    <div className="h-px flex-1 bg-border" />
                    <span className="text-xs font-medium text-muted-foreground">or</span>
                    <div className="h-px flex-1 bg-border" />
                  </div>
                )}

                {/* Option ② — subscribe (secondary). Drop the leading "Or" when
                    option ① (virtual) is hidden, else it reads oddly. */}
                <p className="text-xs text-muted-foreground mb-3">
                  {virtualEvent
                    ? "Or be first to know when we add new events"
                    : "Be first to know when we add new events"}
                </p>
                <form onSubmit={handleNotify} className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="text"
                      placeholder="First name *"
                      aria-label="First name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                      className={inputClass}
                    />
                    <input
                      type="text"
                      placeholder="Last name"
                      aria-label="Last name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className={inputClass}
                    />
                  </div>
                  <input
                    type="email"
                    placeholder="your@email.com *"
                    aria-label="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className={inputClass}
                  />

                  {/* City (required) — type 3+ chars to match known cities
                      (auto-fills country) or countries; country stays editable. */}
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Your city *"
                      aria-label="City"
                      autoComplete="off"
                      value={cityInput}
                      onChange={(e) => {
                        setCityInput(e.target.value);
                        // Only pop the list on real edits — browser autofill
                        // fires a change with inputType insertReplacementText
                        // (or none), and shouldn't open our suggestions.
                        const it = (e.nativeEvent as InputEvent).inputType;
                        setCityOpen(!!it && it !== "insertReplacementText");
                      }}
                      // No auto-open on focus: clicking in or accepting browser
                      // autofill must not open the list; typing (above) does.
                      onBlur={() => setTimeout(() => setCityOpen(false), 200)}
                      required
                      className={inputClass}
                    />
                    {cityOpen && suggestions.length > 0 && (
                      <ul className="absolute z-10 mt-1 w-full rounded-xl border border-border bg-card shadow-lg overflow-hidden">
                        {suggestions.map((c) => (
                          <li key={`${c.city}|${c.country}`}>
                            <button
                              type="button"
                              onMouseDown={(e) => {
                                // mousedown beats input blur — fill city + pre-fill its country.
                                e.preventDefault();
                                setCityInput(c.city);
                                setCountry(c.country);
                                setCityOpen(false);
                              }}
                              className="w-full text-left px-4 py-2.5 text-sm text-foreground hover:bg-brand-orange/10 transition-colors"
                            >
                              {c.city}
                              {c.country && (
                                <span className="ml-1 text-muted-foreground">
                                  {countryToFlag(c.country)}
                                </span>
                              )}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {/* Country — always shown and editable; a city/country
                      suggestion pre-fills it, but it can be changed. */}
                  <select
                    aria-label="Country"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    required
                    className={inputClass}
                  >
                    <option value="">Country *</option>
                    {COUNTRY_NAMES.map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>

                  {error && (
                    <p role="alert" className="text-xs text-red-500 font-medium">{error}</p>
                  )}
                  <button
                    type="submit"
                    disabled={submitting || !firstName.trim() || !email.trim() || !cityValid}
                    className={`${btnFilled} disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {submitting ? (
                      <span className="inline-flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Sending…
                      </span>
                    ) : (
                      "Get Notified"
                    )}
                  </button>
                </form>
              </>
            )}

            <p className="text-xs text-muted-foreground mt-3 text-center leading-relaxed">
              No spam — unsubscribe anytime.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
